import React, { useEffect, useMemo, useState } from "react";
import { adminApi, toApiAbsoluteUrl } from "../../api/client";
import { Link } from "react-router-dom";
import { LinearProgress, Snackbar, Alert } from "@mui/material";
import { ChevronRight, FileText, Edit } from "lucide-react";

export default function ChannelPartners() {
  const [channelPartners, setChannelPartners] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ q: "", page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [editModal, setEditModal] = useState({
    open: false,
    channelPartner: null,
  });
  const [editForm, setEditForm] = useState({
    companyName: "",
    investmentCapacity: "",
    experience: "",
    panNumber: "",
    gstNumber: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
    },
    maxPromoters: 1,
    commissionRate: 0,
    notes: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const normalizedQuery = useMemo(
    () => (filters.q || "").trim().toLowerCase(),
    [filters.q]
  );

  const toAbsoluteUrl = toApiAbsoluteUrl;

  function formatBytes(bytes) {
    if (bytes === 0) return "0 B";
    if (!bytes || Number.isNaN(bytes)) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${
      sizes[i]
    }`;
  }

  function formatDate(iso) {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };
    try {
      setLoading(true);
      const res = await adminApi.listChannelPartners({
        page: Number(merged.page || 1),
        limit: Number(merged.limit || 20),
      });
      setChannelPartners(res?.items || []);
      setTotal(res?.total || 0);
      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      console.error("Channel partners API error:", _e);
      setChannelPartners([]);
      setTotal(0);
      const msg = _e?.data?.message || "Failed to load channel partners";
      setSnackbar({ open: true, message: msg });
    } finally {
      setLoading(false);
    }
  }

  const filteredChannelPartners = useMemo(() => {
    if (!normalizedQuery) return channelPartners;
    return (channelPartners || []).filter((cp) => {
      const email = (cp?.email || "").toLowerCase();
      const phone = (cp?.phoneNumber || "").toLowerCase();
      const company = (
        cp?.channelPartnerProfile?.companyName || ""
      ).toLowerCase();
      const panNumber = (
        cp?.channelPartnerProfile?.panNumber || ""
      ).toLowerCase();
      const gstNumber = (
        cp?.channelPartnerProfile?.gstNumber || ""
      ).toLowerCase();
      const address = cp?.channelPartnerProfile?.address;
      const addressStr = address
        ? `${address.street || ""} ${address.city || ""} ${
            address.state || ""
          } ${address.pincode || ""}`.toLowerCase()
        : "";

      return (
        email.includes(normalizedQuery) ||
        phone.includes(normalizedQuery) ||
        company.includes(normalizedQuery) ||
        panNumber.includes(normalizedQuery) ||
        gstNumber.includes(normalizedQuery) ||
        addressStr.includes(normalizedQuery)
      );
    });
  }, [channelPartners, normalizedQuery]);

  const openEditModal = (channelPartner) => {
    const profile = channelPartner?.channelPartnerProfile || {};
    setEditForm({
      companyName: profile?.companyName || "",
      investmentCapacity: profile?.investmentCapacity || "",
      experience: profile?.experience || "",
      panNumber: profile?.panNumber || "",
      gstNumber: profile?.gstNumber || "",
      address: {
        street: profile?.address?.street || "",
        city: profile?.address?.city || "",
        state: profile?.address?.state || "",
        pincode: profile?.address?.pincode || "",
        country: profile?.address?.country || "India",
      },
      maxPromoters: profile?.maxPromoters || 1,
      commissionRate: profile?.commissionRate || 0,
      notes: profile?.notes || "",
    });
    setEditModal({ open: true, channelPartner });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, channelPartner: null });
    setEditForm({
      companyName: "",
      investmentCapacity: "",
      experience: "",
      panNumber: "",
      gstNumber: "",
      address: {
        street: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
      },
      maxPromoters: 1,
      commissionRate: 0,
      notes: "",
    });
  };

  const handleEditSubmit = async () => {
    try {
      setEditLoading(true);

      // Validate form data
      if (editForm.maxPromoters < 0 || editForm.maxPromoters > 1000) {
        setSnackbar({
          open: true,
          message: "Max promoters must be between 0 and 1000",
        });
        setEditLoading(false);
        return;
      }

      if (editForm.commissionRate < 0 || editForm.commissionRate > 100) {
        setSnackbar({
          open: true,
          message: "Commission rate must be between 0 and 100",
        });
        setEditLoading(false);
        return;
      }

      // Update channel partner settings
      await adminApi.updateChannelPartnerSettings(
        editModal.channelPartner.id,
        editForm
      );
      setSnackbar({
        open: true,
        message: "Channel partner settings updated successfully",
        severity: "success",
      });
      closeEditModal();

      // Force refresh the data
      await runSearch({ page: 1 }); // Force refresh from page 1
    } catch (error) {
      console.error("Update channel partner settings error:", error);
      console.error("Error details:", {
        status: error?.status,
        data: error?.data,
        message: error?.message,
      });
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Failed to update channel partner settings";
      setSnackbar({ open: true, message: errorMessage });
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link to="/dashboard" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">Channel Partners</span>
      </div>

      {/* Search bar */}
      <div className="bg-white shadow rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search company, email, phone, PAN, GST, or address"
          className="border p-2 rounded"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 bg-gray-600 text-white rounded"
            onClick={() => {
              setSearchText("");
              setFilters({ q: "", page: 1, limit: filters.limit });
              runSearch({ page: 1 });
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Responsive table */}
      <div className="overflow-x-auto">
        <table className="w-full text-center bg-white shadow rounded min-w-[1400px]">
          <thead>
            <tr>
              <th className="p-2 border">Company</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Phone</th>
              <th className="p-2 border">Investment</th>
              <th className="p-2 border">Experience</th>
              <th className="p-2 border">PAN</th>
              <th className="p-2 border">GST</th>
              <th className="p-2 border">Address</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Documents</th>
              <th className="p-2 border">Max Promoters</th>
              <th className="p-2 border">Created</th>
              {/* <th className="p-2 border">Updated</th> */}
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredChannelPartners.map((cp) => {
              const company = cp?.channelPartnerProfile?.companyName || "-";
              const docs = cp?.channelPartnerProfile?.documents || [];
              const address = cp?.channelPartnerProfile?.address;
              const addressStr = address
                ? `${address.street || ""}${
                    address.city ? `, ${address.city}` : ""
                  }${address.state ? `, ${address.state}` : ""}${
                    address.pincode ? ` - ${address.pincode}` : ""
                  }`.trim()
                : "-";

              return (
                <tr key={cp.id}>
                  <td className="p-2 border">
                    {company !== "-" ? (
                      <button
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={() => setSelectedRecord(cp)}
                        title="View details"
                      >
                        {company}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="p-2 border text-sm">{cp.email}</td>
                  <td className="p-2 border text-sm">
                    {cp.phoneNumber || "-"}
                  </td>
                  <td className="p-2 border">
                    {cp?.channelPartnerProfile?.investmentCapacity
                      ? `${cp.channelPartnerProfile.investmentCapacity}L`
                      : "-"}
                  </td>
                  <td className="p-2 border">
                    {cp?.channelPartnerProfile?.experience
                      ? `${cp.channelPartnerProfile.experience} years`
                      : "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {cp?.channelPartnerProfile?.panNumber || "-"}
                  </td>
                  <td className="p-2 border text-sm">
                    {cp?.channelPartnerProfile?.gstNumber || "-"}
                  </td>
                  <td
                    className="p-2 border text-sm max-w-[200px] truncate"
                    title={addressStr}
                  >
                    {addressStr}
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        cp?.channelPartnerProfile?.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : cp?.channelPartnerProfile?.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : cp?.channelPartnerProfile?.status === "rejected"
                          ? "bg-red-100 text-red-800"
                          : cp?.channelPartnerProfile?.status === "incomplete"
                          ? "bg-orange-100 text-orange-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {cp?.channelPartnerProfile?.status || "Unknown"}
                    </span>
                  </td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        docs.length > 0
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {docs.length} docs
                    </span>
                  </td>
                  <td className="p-2 border text-sm">
                    {(() => {
                      const maxPromoters =
                        cp?.channelPartnerProfile?.maxPromoters;
                      return maxPromoters || 1;
                    })()}
                  </td>
                  <td className="p-2 border text-sm">
                    {formatDate(cp?.channelPartnerProfile?.createdAt)}
                  </td>
                  {/* <td className="p-2 border text-sm">
                    {formatDate(cp?.channelPartnerProfile?.updatedAt)}
                  </td> */}
                  <td className="p-2 border">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedRecord(cp)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                      >
                        View
                      </button>
                      <button
                        onClick={() => openEditModal(cp)}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm flex items-center gap-1"
                      >
                        <Edit size={14} />
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredChannelPartners.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={14}>
                  No channel partners found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between p-3 flex-wrap gap-2">
        <div className="text-sm text-gray-600">Total: {total}</div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={filters.page <= 1}
            onClick={() =>
              runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
            }
          >
            Prev
          </button>
          <span className="text-sm">Page {filters.page}</span>
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={channelPartners.length < filters.limit}
            onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
          >
            Next
          </button>
          <select
            className="ml-2 border p-1 rounded text-sm"
            value={filters.limit}
            onChange={(e) =>
              runSearch({ limit: Number(e.target.value), page: 1 })
            }
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      {/* Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Channel Partner Details</h3>
            <div className="space-y-4">
              <div>
                <p className="font-semibold">Basic Information</p>
                <p>Email: {selectedRecord.email}</p>
                <p>Phone: {selectedRecord.phoneNumber || "-"}</p>
                <p>Role: {selectedRecord.role}</p>
                <p>Status: {selectedRecord.status || "-"}</p>
              </div>

              {selectedRecord.channelPartnerProfile && (
                <>
                  <div>
                    <p className="font-semibold">Business Information</p>
                    <p>
                      Company:{" "}
                      {selectedRecord.channelPartnerProfile.companyName}
                    </p>
                    <p>
                      Investment Capacity:{" "}
                      {selectedRecord.channelPartnerProfile.investmentCapacity}
                    </p>
                    <p>
                      Experience:{" "}
                      {selectedRecord.channelPartnerProfile.experience}
                    </p>
                    <p>
                      PAN Number:{" "}
                      {selectedRecord.channelPartnerProfile.panNumber}
                    </p>
                    <p>
                      GST Number:{" "}
                      {selectedRecord.channelPartnerProfile.gstNumber || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Address</p>
                    <p>
                      {selectedRecord.channelPartnerProfile.address?.street ||
                        ""}
                      {selectedRecord.channelPartnerProfile.address?.city &&
                        `, ${selectedRecord.channelPartnerProfile.address.city}`}
                      {selectedRecord.channelPartnerProfile.address?.state &&
                        `, ${selectedRecord.channelPartnerProfile.address.state}`}
                      {selectedRecord.channelPartnerProfile.address?.pincode &&
                        ` - ${selectedRecord.channelPartnerProfile.address.pincode}`}
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold">Documents</p>
                    <p>
                      Documents:{" "}
                      {
                        (selectedRecord.channelPartnerProfile.documents || [])
                          .length
                      }
                    </p>
                    <div className="mt-3 max-h-72 overflow-y-auto border rounded">
                      {(selectedRecord.channelPartnerProfile.documents || [])
                        .length === 0 ? (
                        <div className="p-4 text-sm text-gray-500">
                          No documents uploaded
                        </div>
                      ) : (
                        <table className="w-full text-left text-sm min-w-[600px]">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-2 border">Preview</th>
                              <th className="p-2 border">Name</th>
                              <th className="p-2 border">Type</th>
                              <th className="p-2 border">Size</th>
                              <th className="p-2 border">Uploaded</th>
                              <th className="p-2 border">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(
                              selectedRecord.channelPartnerProfile.documents ||
                              []
                            ).map((d) => {
                              const isImage = (d?.mimeType || "").startsWith(
                                "image/"
                              );
                              return (
                                <tr key={d.id}>
                                  <td className="p-2 border align-middle">
                                    {isImage ? (
                                      <button
                                        className="inline-flex items-center"
                                        onClick={() =>
                                          window.open(
                                            toAbsoluteUrl(d.url),
                                            "_blank",
                                            "noopener"
                                          )
                                        }
                                        title="Open image"
                                      >
                                        <img
                                          src={toAbsoluteUrl(d.url)}
                                          alt={
                                            d.originalName ||
                                            d.kind ||
                                            "document"
                                          }
                                          className="h-12 w-12 object-cover rounded"
                                        />
                                      </button>
                                    ) : (
                                      <div className="flex items-center text-gray-600">
                                        <FileText size={18} className="mr-1" />
                                        <span>File</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-2 border align-middle">
                                    {d.originalName || d.kind || "-"}
                                  </td>
                                  <td className="p-2 border align-middle">
                                    {d.mimeType || "-"}
                                  </td>
                                  <td className="p-2 border align-middle">
                                    {formatBytes(d.size)}
                                  </td>
                                  <td className="p-2 border align-middle">
                                    {formatDate(d.uploadedAt)}
                                  </td>
                                  <td className="p-2 border align-middle">
                                    <div className="flex items-center gap-2">
                                      <button
                                        className="px-2 py-1 text-blue-600 hover:text-blue-800 underline"
                                        onClick={() =>
                                          window.open(
                                            toAbsoluteUrl(d.url),
                                            "_blank",
                                            "noopener"
                                          )
                                        }
                                      >
                                        Open
                                      </button>
                                      <a
                                        className="px-2 py-1 text-gray-700 hover:text-gray-900 underline"
                                        href={toAbsoluteUrl(d.url)}
                                        download={d.originalName || true}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Download
                                      </a>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setSelectedRecord(null)}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto my-4">
            <h3 className="text-xl font-bold mb-4">
              Edit Channel Partner Profile
            </h3>
            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={editForm.companyName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, companyName: e.target.value })
                    }
                    className="w-full border p-2 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Investment Capacity (₹ Lakhs)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.investmentCapacity}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        investmentCapacity: e.target.value,
                      })
                    }
                    className="w-full border p-2 rounded"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experience (Years)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={editForm.experience}
                    onChange={(e) =>
                      setEditForm({ ...editForm, experience: e.target.value })
                    }
                    className="w-full border p-2 rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PAN Number
                  </label>
                  <input
                    type="text"
                    value={editForm.panNumber}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        panNumber: e.target.value.toUpperCase(),
                      })
                    }
                    className="w-full border p-2 rounded"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number
                </label>
                <input
                  type="text"
                  value={editForm.gstNumber}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      gstNumber: e.target.value.toUpperCase(),
                    })
                  }
                  className="w-full border p-2 rounded"
                />
              </div>

              {/* Address */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Address
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={editForm.address.street}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            street: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={editForm.address.city}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            city: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={editForm.address.state}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            state: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pincode
                    </label>
                    <input
                      type="text"
                      value={editForm.address.pincode}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            pincode: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      value={editForm.address.country}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          address: {
                            ...editForm.address,
                            country: e.target.value,
                          },
                        })
                      }
                      className="w-full border p-2 rounded"
                    />
                  </div>
                </div>
              </div>

              {/* Settings */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Settings
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Promoters
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="1000"
                      value={editForm.maxPromoters}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          maxPromoters: parseInt(e.target.value) || 0,
                        })
                      }
                      className="w-full border p-2 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Commission Rate (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={editForm.commissionRate}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          commissionRate: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="w-full border p-2 rounded"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm({ ...editForm, notes: e.target.value })
                  }
                  className="w-full border p-2 rounded h-20"
                  placeholder="Internal notes about this channel partner..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:opacity-50"
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loader */}
      {loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <LinearProgress />
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar({ open: false, message: "", severity: "error" })
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setSnackbar({ open: false, message: "", severity: "error" })
          }
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
